/**
 * Calculadora de discos (Req. 26.3, 26.4; design.md, 8.15).
 *
 * Con un peso objetivo, el peso de la barra y los discos que hay, dice qué
 * discos poner de cada lado y, si no da exacto, cuánto falta. Toma los discos
 * de mayor a menor: es como se cargan de verdad, y así quedan los menos
 * discos posibles.
 *
 * Todo el cálculo va en la unidad de los discos (kg o lb), no en kg
 * canónicos: el número que trae grabado un disco es el dato real, y convertir
 * 45 lb a 20.411 kg daría una precisión que no existe. La configuración
 * guarda su unidad y el objetivo se escribe en esa misma unidad.
 *
 * Módulo puro: sin React y sin Dexie.
 */

import { formatearNumero } from './numbers';
import type { UnidadPeso } from './units';

/** Los de un gimnasio común, de mayor a menor. */
export const DISCOS_KG: readonly number[] = [25, 20, 15, 10, 5, 2.5, 1.25];
export const DISCOS_LB: readonly number[] = [45, 35, 25, 10, 5, 2.5];
export const BARRA_KG = 20;
export const BARRA_LB = 45;

/** Tope de lo que se puede escribir, para no calcular barbaridades. */
export const OBJETIVO_MAXIMO = 1000;
/** Un disco tiene que pesar algo y no más que esto. */
export const DISCO_MAXIMO = 100;
/** Cuántos discos iguales caben por lado: más que esto no entra en la barra. */
export const DISCOS_MAXIMOS_POR_LADO = 10;

const CASI = 1e-9;
const numero = (v: number) => formatearNumero(v, { decimales: 2, fijos: false });

export interface BarraYDiscos {
  unidad: UnidadPeso;
  /** Lo que pesa la barra sola. 0 para una máquina o para contar solo los discos. */
  barra: number;
  /** Los que hay, de mayor a menor y sin repetir. */
  discos: readonly number[];
}

export function barraYDiscosPredeterminados(unidad: UnidadPeso): BarraYDiscos {
  return unidad === 'lb'
    ? { unidad: 'lb', barra: BARRA_LB, discos: DISCOS_LB }
    : { unidad: 'kg', barra: BARRA_KG, discos: DISCOS_KG };
}

/** Deja los discos usables: números mayores que cero, sin repetir, de mayor a menor. */
export function discosLimpios(discos: readonly number[]): number[] {
  const unicos = new Set<number>();
  for (const d of discos) {
    if (Number.isFinite(d) && d > 0 && d <= DISCO_MAXIMO) unicos.add(d);
  }
  return [...unicos].sort((a, b) => b - a);
}

export interface DiscoPorLado {
  disco: number;
  cuantos: number;
}

export type MotivoSinDiscos = 'sin_objetivo' | 'menos_que_la_barra' | 'sin_discos';

export type ResultadoDeDiscos =
  | {
      tipo: 'listo';
      /** Los discos de cada lado, de mayor a menor. Vacío = solo la barra. */
      porLado: DiscoPorLado[];
      /** Lo que suman los discos de un lado. */
      porLadoPeso: number;
      /** Barra + los discos de los dos lados: lo que de verdad se levanta. */
      total: number;
      /** Lo que falta para el objetivo; 0 si da exacto. */
      diferencia: number;
      pasos: string[];
    }
  | { tipo: 'no_se_puede'; motivo: MotivoSinDiscos };

/**
 * Qué discos poner de cada lado para llegar al objetivo. Si no da exacto, lo
 * dice con la diferencia: no redondea el objetivo ni inventa un disco que no
 * hay.
 */
export function discosPorLado(objetivo: number, config: BarraYDiscos): ResultadoDeDiscos {
  if (!Number.isFinite(objetivo) || objetivo <= 0 || objetivo > OBJETIVO_MAXIMO) {
    return { tipo: 'no_se_puede', motivo: 'sin_objetivo' };
  }
  const barra = Number.isFinite(config.barra) && config.barra > 0 ? config.barra : 0;
  if (objetivo + CASI < barra) return { tipo: 'no_se_puede', motivo: 'menos_que_la_barra' };
  const discos = discosLimpios(config.discos);
  const falta = objetivo - barra;
  if (discos.length === 0 && falta > CASI) return { tipo: 'no_se_puede', motivo: 'sin_discos' };

  let restante = falta / 2;
  const porLado: DiscoPorLado[] = [];
  for (const disco of discos) {
    const cuantos = Math.min(DISCOS_MAXIMOS_POR_LADO, Math.floor(restante / disco + CASI));
    if (cuantos > 0) {
      porLado.push({ disco, cuantos });
      restante -= cuantos * disco;
    }
  }
  const porLadoPeso = porLado.reduce((suma, d) => suma + d.disco * d.cuantos, 0);
  const total = barra + 2 * porLadoPeso;
  const diferencia = Math.abs(objetivo - total) < CASI ? 0 : objetivo - total;
  const u = config.unidad;
  const n = numero;
  const pasos = [
    barra === 0
      ? `Sin barra: los ${n(objetivo)} ${u} son todo de discos.`
      : `Quita la barra: ${n(objetivo)} − ${n(barra)} = ${n(falta)} ${u} de discos.`,
    `La mitad de cada lado: ${n(falta)} ÷ 2 = ${n(falta / 2)} ${u} por lado.`,
    porLado.length === 0
      ? 'Con los discos que hay no cabe ninguno de ese lado.'
      : `De mayor a menor: ${porLado.map((d) => `${d.cuantos} de ${n(d.disco)}`).join(', ')} = ${n(porLadoPeso)} ${u} por lado.`,
    diferencia === 0
      ? `Los dos lados y la barra suman ${n(total)} ${u}, justo el objetivo.`
      : `Los dos lados y la barra suman ${n(total)} ${u}: ${diferencia > 0 ? 'faltan' : 'sobran'} ${n(
          Math.abs(diferencia),
        )} ${u}.`,
  ];
  return { tipo: 'listo', porLado, porLadoPeso, total, diferencia, pasos };
}

/** "25 + 15", "2 × 25 + 10" o "solo la barra". */
export function textoDiscos(porLado: readonly DiscoPorLado[]): string {
  if (porLado.length === 0) return 'solo la barra';
  return porLado.map((d) => (d.cuantos === 1 ? numero(d.disco) : `${d.cuantos} × ${numero(d.disco)}`)).join(' + ');
}

/** "25 + 15 kg por lado", para leerlo de un golpe. */
export function textoPorLado(porLado: readonly DiscoPorLado[], unidad: UnidadPeso): string {
  if (porLado.length === 0) return 'Solo la barra';
  return `${textoDiscos(porLado)} ${unidad} por lado`;
}

export const MOTIVO_SIN_DISCOS: Readonly<Record<MotivoSinDiscos, string>> = {
  sin_objetivo: 'Escribe el peso que quieres levantar.',
  menos_que_la_barra: 'Ese peso es menor que la barra sola.',
  sin_discos: 'No hay discos configurados: agrega al menos uno.',
};

/** Lo que se puede escribir como peso de un disco o de la barra, o por qué no sirve. */
export function validarPesoDeDisco(valor: number | null): string | null {
  if (valor === null || !Number.isFinite(valor) || valor <= 0) return 'Escribe un peso mayor que cero.';
  if (valor > DISCO_MAXIMO) return `Un disco de más de ${DISCO_MAXIMO} no es real.`;
  return null;
}
